<template>
  <div class="h-cat">
    <!-- Topbar chrome moved to the shell TheTopMenuBar (Plan 007 §2c). -->
    <div class="h-cat__content">
      <!-- ===== Page head ===== -->
      <div class="h-cat__head">
        <div>
          <h1 class="h-cat__title">Catalogue</h1>
          <p class="h-cat__sub">{{ subtitle }}</p>
        </div>
        <div class="h-cat__actions">
          <button class="h-btn h-btn--primary" @click="openNewProduct" v-if="!loading && !errorProducts">
            <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M12 5v14M5 12h14"/></svg>
            New product
          </button>
        </div>
      </div>

      <!-- ===== Error ===== -->
      <HermesEmptyState
        v-if="errorProducts"
        variant="error"
        title="Couldn't load your catalogue"
        :message="errorProducts"
        @retry="loadProducts"
      />

      <!-- ===== Loading ===== -->
      <template v-else-if="loading">
        <div class="h-cat__skel" v-for="n in 2" :key="n">
          <div class="h-cat__skelhead">
            <span class="h-sk h-sk--logo"></span>
            <span class="h-sk" style="width:180px"></span>
          </div>
          <HermesEmptyState variant="loading" :skeleton-rows="3" title="Loading products" />
        </div>
      </template>

      <!-- ===== First run ===== -->
      <HermesEmptyState
        v-else-if="!products.length"
        variant="first-run"
        title="No products yet"
        message="A product is a thing you sell — its plans carry the price, limits and licence terms. Create your first product to start building the catalogue."
      >
        <template #actions>
          <button class="h-btn h-btn--primary" @click="openNewProduct">
            <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M12 5v14M5 12h14"/></svg>
            New product
          </button>
        </template>
      </HermesEmptyState>

      <!-- ===== Product cards ===== -->
      <template v-else>
        <div v-for="(p, idx) in products" :key="p.id" class="h-prod">
          <div class="h-prod__head">
            <div class="h-prod__logo" :style="{ background: glyphColor(idx) }">{{ glyph(p.name) }}</div>
            <div class="h-prod__meta">
              <div class="h-prod__name">
                {{ p.name }}
                <span class="h-typepill" :class="{ 'h-typepill--oneoff': !p.requires_activation }">
                  {{ p.requires_activation ? 'Licenced' : 'One-off' }}
                </span>
              </div>
              <div class="h-prod__subline">
                slug <span class="h-mono">{{ p.slug }}</span>
                <span v-if="p.requires_activation"> · requires activation · signed-JWT keys</span>
                <span v-else> · perpetual licence</span>
              </div>
            </div>
            <div class="h-prod__stats">
              <div class="h-pstat"><div class="h-pstat__v">{{ detail(p.id).stats.plans }}</div><div class="h-pstat__l">Plans</div></div>
              <div class="h-pstat"><div class="h-pstat__v">{{ detail(p.id).stats.active_subscriptions }}</div><div class="h-pstat__l">Subs</div></div>
              <div class="h-pstat">
                <div class="h-pstat__v"><HermesMoneyCell :amount="detail(p.id).stats.mrr" :currency="detail(p.id).stats.currency" /></div>
                <div class="h-pstat__l">MRR</div>
              </div>
              <div class="h-prod__btns">
                <button class="h-btn h-btn--secondary h-btn--sm" @click="openNewPlan(p)">
                  <svg viewBox="0 0 24 24" class="h-btn__i"><path d="M12 5v14M5 12h14"/></svg>Add plan
                </button>
                <div class="h-menu" @keydown.esc="openMenu = null">
                  <button class="h-rowmenu" aria-label="Product actions" @click.stop="toggleMenu(p.id)">⋯</button>
                  <div v-if="openMenu === p.id" class="h-menu__pop" @click.stop>
                    <button @click="openEditProduct(p); openMenu = null">Edit product</button>
                    <button class="h-menu__danger" @click="askDeleteProduct(p); openMenu = null">Delete product</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Plan table -->
          <div v-if="detail(p.id).error" class="h-inline-error">
            {{ detail(p.id).error }} <button class="h-link" @click="loadProductData(p.id)">Retry</button>
          </div>
          <HermesEmptyState v-else-if="detail(p.id).loading" variant="loading" :skeleton-rows="2" title="Loading plans" />
          <div v-else-if="!detail(p.id).plans.length" class="h-empty-mini">
            No plans yet. <button class="h-link" @click="openNewPlan(p)">Add the first plan</button>
          </div>
          <table v-else class="h-ptable">
            <thead>
              <tr>
                <th style="width:200px">Plan</th>
                <th class="h-r" style="width:150px">Price</th>
                <th style="width:210px">Limits</th>
                <th style="width:110px">Subs</th>
                <th style="width:300px">Gateway sync</th>
                <th class="h-r" style="width:130px">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pl in detail(p.id).plans" :key="pl.id">
                <td>
                  <span class="h-tag" :class="tierClass(pl.tier)">{{ tierLabel(pl) }}</span>
                  <span v-if="!pl.is_active" class="h-tag h-tag--muted" style="margin-left:6px">retired</span>
                </td>
                <td class="h-r">
                  <span class="h-price"><HermesMoneyCell :amount="pl.price_amount" :currency="pl.price_currency" /><span class="h-price__int">{{ intervalSuffix(pl.billing_interval) }}</span><span v-if="pl.price_basis" class="h-price__int">{{ pl.price_basis === 'inclusive' ? ' inc. VAT' : ' + VAT' }}</span></span>
                </td>
                <td class="h-limits">{{ limitsSummary(pl.limits) }}</td>
                <td class="h-num">{{ pl.active_subscriptions }} active</td>
                <td>
                  <div class="h-gwsync">
                    <div v-for="gw in gatewayRows(pl)" :key="gw.key" class="h-gwline">
                      <span class="h-gwname"><i :style="{ background: gw.dot }"></i>{{ gw.label }}</span>
                      <span v-if="gw.state === 'synced'" class="h-synced">
                        <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>synced
                      </span>
                      <span v-else-if="gw.state === 'unverified'" class="h-unverified">
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>unverified
                      </span>
                      <span v-else class="h-notsynced">
                        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>{{ gw.state === 'drift' ? 'price drift' : 'not linked' }}
                      </span>
                      <span v-if="gw.objectId && (gw.state === 'synced' || gw.state === 'unverified')" class="h-syncid">{{ gw.objectId }}</span>
                      <button
                        v-if="gw.state === 'drift' || gw.state === 'notlinked'"
                        class="h-syncbtn"
                        :disabled="gw.busy"
                        @click="pushGateway(p.id, pl, gw)"
                      >{{ gw.busy ? '…' : (gw.state === 'drift' ? `Push ${money(pl)}` : gw.createLabel) }}</button>
                    </div>
                  </div>
                </td>
                <td class="h-r">
                  <div class="h-rowact">
                    <button class="h-linkbtn" @click="openEditPlan(p, pl)">Edit</button>
                    <button class="h-linkbtn h-linkbtn--danger" @click="askDeletePlan(p, pl)">Delete</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <!-- ===== Plan editor drawer ===== -->
    <HermesPlanEditorDrawer
      v-model="planDrawer.open"
      :plan="planDrawer.plan"
      :product-id="planDrawer.productId"
      :product-name="planDrawer.productName"
      :default-currency="planDrawer.currency"
      :api-base="apiBase"
      @saved="onPlanSaved"
    />

    <!-- ===== Product editor drawer ===== -->
    <HermesDrawer v-model="prodDrawer.open" :title="prodDrawer.id ? 'Edit product' : 'New product'">
      <template #header>
        <div>
          <h3 class="h-pd__title">{{ prodDrawer.id ? 'Edit product' : 'New product' }}</h3>
          <div class="h-pd__sub" v-if="prodDrawer.id">{{ prodDrawer.slug }}</div>
        </div>
      </template>
      <div class="h-pd">
        <div class="h-pd__field">
          <label class="h-pd__label">Product name <span class="h-pd__req">*</span></label>
          <input class="h-pd__input" type="text" v-model="prodDrawer.name" />
        </div>
        <div class="h-pd__field">
          <label class="h-pd__label">Slug <span class="h-pd__req">*</span></label>
          <input class="h-pd__input h-mono" type="text" v-model="prodDrawer.slug" :disabled="!!prodDrawer.id" />
          <p class="h-pd__hint" v-if="prodDrawer.id">The slug is fixed once a product has plans and licences.</p>
        </div>
        <div class="h-pd__field">
          <label class="h-pd__label">Description</label>
          <textarea class="h-pd__input" rows="3" v-model="prodDrawer.description"></textarea>
        </div>
        <label class="h-pe__switch">
          <input type="checkbox" v-model="prodDrawer.requires_activation" />
          <span class="h-pe__track"></span>
          <span class="h-pe__swlbl">Requires activation (licenced product — issues signed keys)</span>
        </label>
        <label class="h-pe__switch">
          <input type="checkbox" v-model="prodDrawer.is_active" />
          <span class="h-pe__track"></span>
          <span class="h-pe__swlbl">Product is active</span>
        </label>
        <p v-if="prodDrawer.error" class="h-pe__saveerr">{{ prodDrawer.error }}</p>
      </div>
      <template #footer>
        <button class="h-btn h-btn--ghost" @click="prodDrawer.open = false">Cancel</button>
        <button class="h-btn h-btn--primary" :disabled="prodDrawer.saving" @click="saveProduct">
          {{ prodDrawer.id ? 'Save' : 'Create product' }}
        </button>
      </template>
    </HermesDrawer>

    <!-- ===== Delete confirmations ===== -->
    <HermesConfirmModal
      v-model="planDelete.open"
      title="Delete plan"
      :description="planDelete.desc"
      confirm-word="DELETE"
      confirm-label="Delete plan"
      @confirm="confirmDeletePlan"
    />
    <HermesConfirmModal
      v-model="productDelete.open"
      title="Delete product"
      :description="productDelete.desc"
      :confirm-word="productDelete.word"
      confirm-label="Delete product"
      @confirm="confirmDeleteProduct"
    />

    <!-- ===== Toasts ===== -->
    <div class="h-toasts" aria-live="polite">
      <div v-for="t in toasts" :key="t.id" class="h-toast" :class="`h-toast--${t.kind}`">{{ t.text }}</div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesCatalogue — Phase 2 products & plans management island
// (admin-catalogue.html). Server-driven: HermesCataloguePage mounts it by NAME
// with componentData.apiBase (+ vendorName). It loads the vendor's products,
// then per-product loads rollup stats + plans (with the gateway-sync column),
// and drives create/edit/delete for both products and plans through the Phase 2
// admin API. Money via HermesMoneyCell; drawers/modals from the Phase 0 kit.
export default {
  name: 'HermesCatalogue',
  props: {
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      apiBase: this.componentData?.apiBase || '/api/v1',
      vendorName: this.componentData?.vendorName || '',

      products: [],
      loading: true,
      errorProducts: null,

      // productId -> { stats, plans, loading, error }
      details: {},

      openMenu: null,

      planDrawer: { open: false, plan: null, productId: null, productName: '', currency: 'GBP' },
      prodDrawer: this.blankProdDrawer(),
      planDelete: { open: false, productId: null, planId: null, desc: '' },
      productDelete: { open: false, id: null, word: '', desc: '' },

      toasts: [],
      toastSeq: 0,

      crumbs: [{ label: 'Commerce' }, { label: 'Catalogue' }],
      glyphColors: ['#0EA5E9', '#6366F1', '#0D9488', '#F59E0B', '#E11D48', '#7C3AED'],
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const nP = this.products.length;
      const nPlans = Object.values(this.details).reduce((a, d) => a + (d?.plans?.length || 0), 0);
      const cur = this.primaryCurrency;
      const bits = [`${nP} product${nP === 1 ? '' : 's'}`, `${nPlans} plan${nPlans === 1 ? '' : 's'}`];
      if (cur) bits.push(`billing in ${cur}`);
      return bits.join(' · ');
    },
    primaryCurrency() {
      for (const d of Object.values(this.details)) {
        if (d?.stats?.currency) return d.stats.currency;
      }
      return 'GBP';
    },
  },
  methods: {
    blankProdDrawer() {
      return { open: false, id: null, slug: '', name: '', description: '', requires_activation: true, is_active: true, saving: false, error: '' };
    },
    detail(id) {
      return this.details[id] || { stats: { plans: 0, active_subscriptions: 0, mrr: '0.00', currency: 'GBP' }, plans: [], loading: true, error: null };
    },

    async loadProducts() {
      this.loading = true;
      this.errorProducts = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/products`);
        // Show active products (soft-deleted ones are hidden from the catalogue).
        this.products = (data.items || []).filter((p) => p.is_active == 1 || p.is_active === true);
        this.details = {};
        for (const p of this.products) {
          this.details[p.id] = { stats: { plans: 0, active_subscriptions: 0, mrr: '0.00', currency: 'GBP' }, plans: [], loading: true, error: null };
        }
        this.loading = false;
        // Fetch each product's rollups + plans in parallel.
        this.products.forEach((p) => this.loadProductData(p.id));
      } catch (e) {
        this.errorProducts = this.msg(e, 'catalogue');
        this.loading = false;
      }
    },

    async loadProductData(id) {
      const d = this.details[id] || { stats: {}, plans: [], loading: true, error: null };
      d.loading = true;
      d.error = null;
      this.details[id] = d;
      try {
        const [detail, plans] = await Promise.all([
          this.api.get(`${this.apiBase}/admin/products/${id}`),
          this.api.get(`${this.apiBase}/admin/products/${id}/plans`),
        ]);
        this.details[id] = {
          stats: detail.stats || { plans: 0, active_subscriptions: 0, mrr: '0.00', currency: 'GBP' },
          plans: plans.items || [],
          loading: false,
          error: null,
        };
      } catch (e) {
        this.details[id] = { ...this.detail(id), loading: false, error: this.msg(e, 'plans') };
      }
    },

    // ── Product drawer ──
    openNewProduct() {
      this.prodDrawer = this.blankProdDrawer();
      this.prodDrawer.open = true;
    },
    openEditProduct(p) {
      this.prodDrawer = {
        open: true, id: p.id, slug: p.slug, name: p.name,
        description: p.description || '',
        requires_activation: !!p.requires_activation,
        is_active: p.is_active !== false && p.is_active != 0,
        saving: false, error: '',
      };
    },
    async saveProduct() {
      const d = this.prodDrawer;
      if (!d.name.trim()) { d.error = 'A product name is required.'; return; }
      d.saving = true; d.error = '';
      try {
        if (d.id) {
          await this.api.patch(`${this.apiBase}/admin/products/${d.id}`, {
            name: d.name.trim(), description: d.description,
            requires_activation: d.requires_activation, is_active: d.is_active,
          });
          this.toast('Product updated', 'success');
        } else {
          await this.api.post(`${this.apiBase}/admin/products`, {
            name: d.name.trim(), slug: d.slug.trim() || d.name.trim(),
            description: d.description, requires_activation: d.requires_activation, is_active: d.is_active,
          });
          this.toast('Product created', 'success');
        }
        this.prodDrawer.open = false;
        await this.loadProducts();
      } catch (e) {
        d.error = this.serverMsg(e) || this.msg(e, 'product save');
      } finally {
        d.saving = false;
      }
    },
    askDeleteProduct(p) {
      this.productDelete = {
        open: true, id: p.id, word: p.slug,
        desc: `This retires "${p.name}" (soft delete). Type the slug ${p.slug} to confirm. Blocked while it has active plans or live subscriptions.`,
      };
    },
    async confirmDeleteProduct() {
      const id = this.productDelete.id;
      try {
        await this.api.delete(`${this.apiBase}/admin/products/${id}`);
        this.toast('Product retired', 'success');
        await this.loadProducts();
      } catch (e) {
        this.toast(this.serverMsg(e) || 'Delete failed', 'error');
      }
    },

    // ── Plan drawer ──
    openNewPlan(p) {
      this.planDrawer = { open: true, plan: null, productId: p.id, productName: p.name, currency: this.detail(p.id).stats.currency || 'GBP' };
    },
    openEditPlan(p, pl) {
      this.planDrawer = { open: true, plan: pl, productId: p.id, productName: p.name, currency: pl.price_currency || 'GBP' };
    },
    onPlanSaved(saved) {
      this.toast('Plan saved', 'success');
      // Refresh the owning product's plans + rollups.
      const pid = saved?.product_id || this.planDrawer.productId;
      if (pid) this.loadProductData(pid);
    },
    askDeletePlan(p, pl) {
      this.planDelete = {
        open: true, productId: p.id, planId: pl.id,
        desc: `This retires the "${pl.name}" plan (soft delete). Blocked while any live subscription still references it.`,
      };
    },
    async confirmDeletePlan() {
      const { productId, planId } = this.planDelete;
      try {
        await this.api.delete(`${this.apiBase}/admin/plans/${planId}`);
        this.toast('Plan retired', 'success');
        this.loadProductData(productId);
      } catch (e) {
        this.toast(this.serverMsg(e) || 'Delete failed', 'error');
      }
    },

    // ── Gateway push / create ──
    async pushGateway(productId, plan, gw) {
      gw.busy = true;
      const url = gw.key === 'stripe'
        ? `${this.apiBase}/admin/plans/${plan.id}/sync-stripe-price`
        : `${this.apiBase}/admin/plans/${plan.id}/sync-revolut-plan?force=1`;
      try {
        await this.api.post(url);
        this.toast(`${gw.label} synced`, 'success');
        this.loadProductData(productId);
      } catch (e) {
        this.toast(`${gw.label}: ${this.serverMsg(e) || 'sync unavailable in this environment'}`, 'error');
        gw.busy = false;
      }
    },

    // ── Gateway status → row model ──
    gatewayRows(plan) {
      const g = plan.gateway || {};
      return [
        this.gwRow('stripe', 'Stripe price', '#635BFF', g.stripe, 'Create price'),
        this.gwRow('revolut', 'Revolut var.', '#191C1F', g.revolut, 'Create SKU'),
      ];
    },
    gwRow(key, label, dot, status, createLabel) {
      const s = status || { linked: false, checked: false };
      let state = 'notlinked';
      if (!s.linked) state = 'notlinked';
      else if (!s.checked) state = 'unverified';
      else if (s.drift) state = 'drift';
      else state = 'synced';
      return { key, label, dot, state, objectId: s.object_id || '', createLabel, busy: false };
    },

    // ── Formatting ──
    glyph(name) {
      const words = (name || '').trim().split(/\s+/);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return (name || '?').slice(0, 2);
    },
    glyphColor(idx) {
      return this.glyphColors[idx % this.glyphColors.length];
    },
    tierLabel(pl) {
      if (pl.tier) return pl.tier.charAt(0).toUpperCase() + pl.tier.slice(1);
      return pl.name;
    },
    tierClass(tier) {
      const t = (tier || '').toLowerCase();
      if (t === 'enterprise' || t === 'pro') return 'h-tag--ent';
      if (t === 'team' || t === 'agency' || t === 'growth') return 'h-tag--team';
      return 'h-tag--starter';
    },
    intervalSuffix(interval) {
      return { monthly: ' /mo', quarterly: ' /qtr', yearly: ' /yr', one_time: ' once' }[interval] || '';
    },
    money(pl) {
      const sym = { GBP: '£', USD: '$', EUR: '€' }[pl.price_currency] || '';
      return `${sym}${Number(pl.price_amount).toFixed(2)}`;
    },
    limitsSummary(limits) {
      if (!limits || typeof limits !== 'object') return '—';
      const keys = Object.keys(limits);
      if (!keys.length) return 'no limits';
      return keys.slice(0, 3).map((k) => {
        const v = limits[k];
        const short = k.replace(/_/g, ' ');
        return v === null || v === undefined ? `${short}: ∞` : `${this.compact(v)} ${short}`;
      }).join(' · ') + (keys.length > 3 ? ` +${keys.length - 3}` : '');
    },
    compact(v) {
      const n = Number(v);
      if (Number.isNaN(n)) return v;
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
      return `${n}`;
    },
    toggleMenu(id) {
      this.openMenu = this.openMenu === id ? null : id;
    },

    // ── Errors / toasts ──
    msg(e, what) {
      const status = e?.response?.status;
      return `Failed to load ${what}${status ? ` (${status})` : ''}. ${e?.message || ''}`.trim();
    },
    serverMsg(e) {
      return e?.response?.data?.message || null;
    },
    toast(text, kind = 'success') {
      const id = ++this.toastSeq;
      this.toasts.push({ id, text, kind });
      setTimeout(() => {
        this.toasts = this.toasts.filter((t) => t.id !== id);
      }, 3600);
    },
    onDocClick() {
      this.openMenu = null;
    },
  },
  mounted() {
    this.loadProducts();
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
};
</script>

<style scoped>
.h-cat { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-cat__topbar { position: sticky; top: 0; z-index: 5; height: 56px; background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); display: flex; align-items: center; gap: 16px; padding: 0 24px; }
.h-cat__topbar-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
.h-cat__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }

.h-cat__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-cat__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-cat__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-cat__actions { display: flex; gap: 10px; align-items: center; }
.h-mono { font-family: var(--h-font-mono, monospace); }

.h-btn { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; line-height: 1; white-space: nowrap; color: var(--h-text-primary); }
.h-btn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.h-btn__i { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.h-btn--primary { background: var(--h-accent, #0EA5E9); color: #fff; }
.h-btn--primary:hover:not(:disabled) { background: var(--h-accent-hover, #0284C7); }
.h-btn--secondary { background: var(--h-bg-card, #fff); color: var(--h-text-primary); border-color: var(--h-border-strong, #CBD5E1); }
.h-btn--secondary:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-btn--ghost { background: transparent; color: var(--h-text-secondary, #64748B); }
.h-btn--ghost:hover { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-primary); }
.h-btn--sm { font-size: 12.5px; padding: 6px 11px; }

/* Product card */
.h-prod { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; margin-bottom: 20px; }
.h-prod__head { display: flex; align-items: center; gap: 14px; padding: 16px 20px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-prod__logo { width: 40px; height: 40px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 15px; color: #fff; flex: none; }
.h-prod__meta { min-width: 0; }
.h-prod__name { font-size: 16px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
.h-prod__subline { font-size: 12.5px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }
.h-prod__stats { margin-left: auto; display: flex; gap: 26px; align-items: center; }
.h-pstat { text-align: right; }
.h-pstat__v { font-size: 16px; font-weight: 600; font-variant-numeric: tabular-nums; }
.h-pstat__l { font-size: 11px; color: var(--h-text-tertiary, #94A3B8); text-transform: uppercase; letter-spacing: 0.04em; }
.h-prod__btns { display: flex; align-items: center; gap: 6px; }

.h-typepill { font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; padding: 2px 8px; border-radius: 5px; background: var(--h-accent-light, #E0F2FE); color: var(--h-accent-dark, #0369A1); }
.h-typepill--oneoff { background: var(--h-neutral-bg, #F1F5F9); color: var(--h-neutral-text, #475569); }

/* row menu */
.h-menu { position: relative; }
.h-rowmenu { color: var(--h-text-tertiary, #94A3B8); cursor: pointer; background: none; border: none; font-size: 16px; padding: 2px 8px; border-radius: var(--h-radius, 6px); }
.h-rowmenu:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-menu__pop { position: absolute; right: 0; top: 28px; z-index: 20; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); box-shadow: var(--h-shadow-panel); min-width: 150px; padding: 4px; }
.h-menu__pop button { display: block; width: 100%; text-align: left; font: inherit; font-size: 13px; padding: 8px 10px; border: none; background: none; color: var(--h-text-primary, #0F172A); cursor: pointer; border-radius: 4px; }
.h-menu__pop button:hover { background: var(--h-bg-subtle, #F1F5F9); }
.h-menu__danger { color: var(--h-danger-text, #B91C1C) !important; }

/* plan table */
.h-ptable { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.h-ptable thead th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; color: var(--h-text-secondary, #64748B); background: var(--h-bg-subtle, #F1F5F9); padding: 10px 20px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-ptable thead th.h-r, .h-ptable td.h-r { text-align: right; }
.h-ptable tbody td { padding: 12px 20px; border-bottom: 1px solid var(--h-border, #E2E8F0); vertical-align: middle; }
.h-ptable tbody tr:last-child td { border-bottom: none; }
.h-ptable tbody tr:hover { background: var(--h-bg-hover, #F8FAFC); }
.h-num { font-variant-numeric: tabular-nums; font-family: var(--h-font-mono, monospace); font-size: 13px; white-space: nowrap; color: var(--h-text-secondary, #64748B); }
.h-price { display: inline-flex; align-items: baseline; gap: 2px; font-weight: 600; white-space: nowrap; }
.h-price__int { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); font-weight: 400; }
.h-limits { font-size: 12.5px; color: var(--h-text-secondary, #64748B); }

.h-tag { border-radius: 5px; padding: 2px 8px; font-size: 11px; font-weight: 600; display: inline-block; }
.h-tag--starter { background: var(--h-neutral-bg, #F1F5F9); color: var(--h-neutral-text, #475569); }
.h-tag--team { background: var(--h-info-bg, #E0F2FE); color: var(--h-info-text, #0369A1); }
.h-tag--ent { background: var(--h-apexl-gold-light, #F6F1E7); color: var(--h-apexl-gold-dark, #8B7355); }
.h-tag--muted { background: transparent; border: 1px solid var(--h-border, #E2E8F0); color: var(--h-text-tertiary, #94A3B8); }

/* gateway sync cell */
.h-gwsync { display: flex; flex-direction: column; gap: 5px; }
.h-gwline { display: flex; align-items: center; gap: 7px; font-size: 12px; }
.h-gwname { display: inline-flex; align-items: center; gap: 6px; color: var(--h-text-secondary, #64748B); min-width: 108px; }
.h-gwname i { width: 8px; height: 8px; border-radius: 2px; }
.h-synced { color: var(--h-success-text, #047857); display: inline-flex; align-items: center; gap: 4px; font-weight: 500; }
.h-synced svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2.6; }
.h-notsynced { display: inline-flex; align-items: center; gap: 5px; color: var(--h-warning-text, #B45309); font-weight: 500; }
.h-notsynced svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2.4; }
.h-unverified { display: inline-flex; align-items: center; gap: 5px; color: var(--h-text-tertiary, #94A3B8); font-weight: 500; }
.h-unverified svg { width: 12px; height: 12px; stroke: currentColor; fill: none; stroke-width: 2.2; }
.h-syncbtn { font: inherit; font-size: 11px; font-weight: 600; color: var(--h-accent-dark, #0369A1); background: var(--h-accent-light, #E0F2FE); border: none; border-radius: 5px; padding: 2px 8px; cursor: pointer; margin-left: 2px; }
.h-syncbtn:disabled { opacity: 0.6; cursor: default; }
.h-syncbtn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-syncid { font-family: var(--h-font-mono, monospace); font-size: 11px; color: var(--h-text-tertiary, #94A3B8); }

.h-rowact { display: flex; gap: 6px; justify-content: flex-end; }
.h-linkbtn { font: inherit; font-size: 12.5px; font-weight: 600; color: var(--h-accent-dark, #0369A1); background: none; border: none; cursor: pointer; padding: 6px 8px; border-radius: 5px; }
.h-linkbtn:hover { background: var(--h-accent-light, #E0F2FE); }
.h-linkbtn:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-linkbtn--danger { color: var(--h-danger-text, #B91C1C); }
.h-linkbtn--danger:hover { background: var(--h-danger-bg, #FEF2F2); }

/* inline states */
.h-inline-error { font-size: 13px; color: var(--h-danger-text, #B91C1C); padding: 16px 20px; }
.h-link { background: none; border: none; color: var(--h-accent-dark, #0369A1); font: inherit; font-weight: 600; cursor: pointer; padding: 0 4px; }
.h-empty-mini { font-size: 13px; color: var(--h-text-tertiary, #94A3B8); text-align: center; padding: 26px 10px; }

/* skeletons */
.h-cat__skel { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; margin-bottom: 20px; }
.h-cat__skelhead { display: flex; align-items: center; gap: 14px; padding: 16px 20px; border-bottom: 1px solid var(--h-border, #E2E8F0); }
.h-sk { height: 14px; border-radius: 5px; background: linear-gradient(90deg, var(--h-bg-subtle, #F1F5F9) 25%, var(--h-border, #E2E8F0) 37%, var(--h-bg-subtle, #F1F5F9) 63%); background-size: 400% 100%; animation: h-cat-sh 1.4s ease infinite; }
.h-sk--logo { width: 40px; height: 40px; border-radius: 9px; }
@keyframes h-cat-sh { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }

/* product drawer body */
.h-pd__title { margin: 0; font-size: 15px; font-weight: 600; }
.h-pd__sub { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; font-family: var(--h-font-mono, monospace); }
.h-pd__field { margin-bottom: 16px; }
.h-pd__label { display: block; font-size: 13px; font-weight: 500; margin-bottom: 6px; }
.h-pd__req { color: var(--h-danger, #EF4444); margin-left: 2px; }
.h-pd__hint { font-size: 12px; color: var(--h-text-tertiary, #94A3B8); margin: 6px 0 0; }
.h-pd__input { width: 100%; font: inherit; font-size: 14px; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); color: var(--h-text-primary, #0F172A); border-radius: var(--h-radius, 6px); padding: 9px 12px; box-sizing: border-box; }
.h-pd__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft, 0 0 0 3px rgba(14,165,233,.15)); }
.h-pd__input:disabled { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-tertiary, #94A3B8); }

/* toggles reused from plan drawer look */
.h-pe__switch { position: relative; display: flex; align-items: center; gap: 12px; cursor: pointer; margin-bottom: 12px; }
.h-pe__switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.h-pe__track { width: 38px; height: 22px; background: var(--h-border-strong, #CBD5E1); border-radius: 999px; position: relative; transition: background 0.15s; flex: none; }
.h-pe__track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; background: #fff; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: transform 0.15s; }
.h-pe__switch input:checked + .h-pe__track { background: var(--h-accent, #0EA5E9); }
.h-pe__switch input:checked + .h-pe__track::after { transform: translateX(16px); }
.h-pe__switch input:focus-visible + .h-pe__track { box-shadow: var(--h-focus-ring); }
.h-pe__swlbl { font-size: 13.5px; }
.h-pe__saveerr { margin-top: 14px; padding: 10px 12px; background: var(--h-danger-bg, #FEF2F2); border: 1px solid var(--h-danger-border, #FECACA); border-radius: var(--h-radius, 6px); font-size: 12.5px; color: var(--h-danger-text, #B91C1C); }

/* toasts */
.h-toasts { position: fixed; bottom: 24px; right: 24px; z-index: 1400; display: flex; flex-direction: column; gap: 10px; }
.h-toast { padding: 11px 16px; border-radius: var(--h-radius, 6px); font-size: 13px; font-weight: 500; box-shadow: var(--h-shadow-panel); border: 1px solid transparent; }
.h-toast--success { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-color: var(--h-success-border, #A7F3D0); }
.h-toast--error { background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-color: var(--h-danger-border, #FECACA); }
</style>
